import React from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const DIGEST_HOURS = [6, 7, 8, 9, 10, 11];
export const DEFAULT_DIGEST_HOUR = 8;

interface SummaryDigestFieldProps {
  enabled: boolean;
  hour: number;
  onEnabledChange: (enabled: boolean) => void;
  onHourChange: (hour: number) => void;
  disabled?: boolean;
}

const SummaryDigestField: React.FC<SummaryDigestFieldProps> = ({
  enabled,
  hour,
  onEnabledChange,
  onHourChange,
  disabled = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Switch
          id="summary-digest-switch"
          checked={enabled}
          onCheckedChange={onEnabledChange}
          disabled={disabled}
        />
        <Label htmlFor="summary-digest-switch">
          {t(
            'agent.settings.notifications.digest_toggle.translation',
            'Daily digest for call summaries'
          )}
        </Label>
      </div>
      <p className="px-1 text-xs text-muted-foreground">
        {t(
          'agent.settings.notifications.digest_toggle_hint.translation',
          "Get one email summarizing the day's calls instead of a separate email per call."
        )}
      </p>
      {enabled && (
        <div className="flex flex-col gap-1.5 pl-1 pt-1">
          <div className="flex items-center gap-2">
            <Label htmlFor="summary-digest-hour">
              {t(
                'agent.settings.notifications.digest_hour.translation',
                'Send at'
              )}
            </Label>
            <Select
              value={String(hour)}
              onValueChange={value => onHourChange(Number(value))}
              disabled={disabled}
            >
              <SelectTrigger
                id="summary-digest-hour"
                className="w-[110px] bg-card"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DIGEST_HOURS.map(h => (
                  <SelectItem key={h} value={String(h)}>
                    {`${String(h).padStart(2, '0')}:00`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="px-1 text-xs text-muted-foreground">
            {t(
              'agent.settings.notifications.digest_hour_hint.translation',
              "Shown in this agent's local time."
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default SummaryDigestField;
